// /src/hooks/useBaselineItemActions.js

import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { trackPromise } from "react-promise-tracker";
import { useTracking } from "react-tracking";

import {
	createBaselineItem,
	updateBaselineItem,
	deleteBaselineItem,
	copyBaselineItem,
	updateItemStatus,
	retrieveOneBaselineItem,
} from "../slices/baselineSlice";

import { useDialogBox } from "../hooks/useDialogBox";

import { Session } from "../utils/sessionUtils";
import { debugLog, errorLog } from "../utils/debugUtils";
import { prepareBaselineItemObject } from "../utils/baselineUtils";

/**
 * Hook for Baseline Item CRUD and status actions, mirroring useBaselineActions.
 * This shared item path is also used by Scenario Selections via BaselineItemConfigs,
 * so error-handling changes here affect both Baseline and Scenario item detail UX.
 * Accepts all supporting functions/state as arguments for testability/side effects.
 */
export function useBaselineItemActions({
	fetchBaselineByIdOrUuid,
	fetchBaselineItems,
	clearDataError,
	handleBackToList,
	currBaseline,
	localItem,
	activeStep,
	selectedItem,
	lookups,
	setLocalItem,
}) {
	const dispatch = useDispatch();
	const { trackEvent } = useTracking();
	const { showDialog, withError } = useDialogBox();

	const reloadParentBaseline = useCallback(
		(baselineId = currBaseline?.id ?? localItem?.baseline_id ?? null) => {
			// Re-read the parent Baseline after list/detail item mutations so Step 3
			// reflects server-authoritative rows instead of leaving stale local data behind.
			if (baselineId != null) {
				fetchBaselineByIdOrUuid && fetchBaselineByIdOrUuid(baselineId);
			}
			fetchBaselineItems && fetchBaselineItems();
		},
		[fetchBaselineByIdOrUuid, fetchBaselineItems, currBaseline?.id, localItem?.baseline_id]
	);

	// The page-level Baseline/Scenario retrieve watchers do not see this nested
	// post-create item reload, so surface the follow-up fetch failure here.
	// Retry is safe because this callback only re-reads the newly created item.
	const surfaceItemReloadError = useCallback(
		(error, itemId, retryAction) => {
			errorLog(2, "[useBaselineItemActions][FLOW] retrieveOneBaselineItem failed", {
				id: itemId,
				error,
			});
			console.error("retrieveOneBaselineItem failed", error);
			withError(error, retryAction, {
				fallbackMessage: "Baseline item retrieve failed.",
			});
		},
		[withError]
	);

	const reloadCreatedItem = useCallback(
		(itemId) => {
			dispatch(retrieveOneBaselineItem(itemId))
				.unwrap()
				.then((res) => {
					debugLog(4, "[useBaselineItemActions][PROCESS] retrieveOneBaselineItem result", {
						retrievedItem: res,
					});

					setLocalItem(prepareBaselineItemObject(currBaseline, res, lookups));
				})
				.catch((err) => {
					surfaceItemReloadError(err, itemId, () => reloadCreatedItem(itemId));
				});
		},
		[dispatch, currBaseline, lookups, setLocalItem, surfaceItemReloadError]
	);

	// CREATE
	const createItemAction = useCallback((overrideItem = null) => {
		const targetItem = overrideItem || localItem;
		debugLog(3, "[useBaselineItemActions][ACTION] createItemAction", { localItem: targetItem });
		debugLog(2, "[useBaselineItemActions][FLOW] createBaselineItem started", {});

		trackPromise(
			dispatch(createBaselineItem({ data: targetItem }))
				.unwrap()
				.then((response) => {
					debugLog(3, "[useBaselineItemActions][STATE] baseline item created", {
						response: response,
						inputItem: targetItem,
					});

					showDialog(
						{
							title: "Successful",
							message: [
								"The Baseline Item was created successfully.",
								`Please continue adding more data for this new Item "${response.name}."`,
							],
						},
						() => {
							reloadParentBaseline(response.baseline_id);

							// reload Baseline Item form the database to get baseline_item_configs
							reloadCreatedItem(response.id);

							clearDataError && clearDataError();
						}
					);

					trackEvent({ event: "createBaselineItem", props: { to: targetItem } });
				})
				.catch((error) => {
					errorLog(2, "[useBaselineItemActions][FLOW] createBaselineItem failed", {
						error: error,
					});
					console.error("createBaselineItem failed", error);
					// Item create is a mutation, so keep the shared error contract
					// without offering retry after a failed response.
					withError(error, null, {
						fallbackMessage: "Baseline item create failed.",
					});
				})
		);
	}, [
		dispatch,
		localItem,
		clearDataError,
		reloadParentBaseline,
		reloadCreatedItem,
		showDialog,
		trackEvent,
		withError,
	]);

	// UPDATE
	const updateItemAction = useCallback(
		(step = -1, overrideItem = null) => {
			const targetItem = overrideItem || localItem;
			debugLog(3, "[useBaselineItemActions][ACTION] updateItemAction", {
				step: step,
				localItem: targetItem,
			});
			debugLog(2, "[useBaselineItemActions][FLOW] updateBaselineItem started", {
				id: targetItem?.id,
			});

			const itemForSave = { ...targetItem };

			trackPromise(
				dispatch(
					updateBaselineItem({
						id: itemForSave.id,
						data: itemForSave,
					})
				)
					.unwrap()
					.then((response) => {
						debugLog(3, "[useBaselineItemActions][STATE] baseline item updated", {
							response: response,
							step: step,
						});
					})
					.then(() => {
						showDialog(
							{
								title: "Successful",
								message: [`The Baseline Item ${itemForSave.name} was updated successfully!`],
							},
							() => {
								reloadParentBaseline(itemForSave.baseline_id);
								// Re-read the saved item so server-derived fields like data_status_id
								// refresh in the open detail form without forcing a close/reopen.
								reloadCreatedItem(itemForSave.id);
								clearDataError && clearDataError();
							}
						);

						trackEvent({
							event: `updateBaselineItem-${itemForSave.id}`,
							props: { to: itemForSave },
						});
					})
					.catch((error) => {
						errorLog(2, "[useBaselineItemActions][FLOW] updateBaselineItem failed", {
							id: itemForSave?.id,
							error: error,
						});
						console.error("updateBaselineItem failed", error);
						// Item update is also a mutation, so keep the shared contract
						// without offering retry after a failed response.
						withError(error, null, {
							fallbackMessage: "Baseline item update failed.",
						});
					})
			);
		},
		[
			dispatch,
			localItem,
			clearDataError,
			reloadParentBaseline,
			reloadCreatedItem,
			showDialog,
			trackEvent,
			withError,
		]
	);

	// DELETE (purge)
	const deleteItemAction = useCallback(
		(id) => {
			debugLog(3, "[useBaselineItemActions][ACTION] deleteItemAction", { id: id });
			debugLog(2, "[useBaselineItemActions][FLOW] deleteBaselineItem started", { id: id });

			trackPromise(
				dispatch(deleteBaselineItem({ data: { id, updatedBy: Session().currUser.id } }))
					.unwrap()
					.then(() => {
						debugLog(3, "[useBaselineItemActions][STATE] baseline item deleted", { id: id });
						showDialog(
							{
								title: "Successful",
								message: ["The Baseline item was purged successfully."],
							},
							() => {
								reloadParentBaseline();
								handleBackToList && handleBackToList();
							}
						);

						trackEvent({ event: `Baseline Item (#${id}) was purged`, props: null });
					})
					.catch((error) => {
						errorLog(2, "[useBaselineItemActions][FLOW] deleteBaselineItem failed", {
							id: id,
							error: error,
						});
						console.error("deleteBaselineItem failed", error);
						// Item delete is a mutation, so keep the shared contract but
						// avoid retry; the purge may already have succeeded.
						withError(error, null, {
							fallbackMessage: "Baseline item delete failed.",
						});
					})
			);
		},
		[
			dispatch,
			showDialog,
			reloadParentBaseline,
			handleBackToList,
			trackEvent,
			withError,
		]
	);

	// COPY
	const copyItemAction = useCallback(
		(id) => {
			debugLog(3, "[useBaselineItemActions][ACTION] copyItemAction", { id: id });
			debugLog(2, "[useBaselineItemActions][FLOW] copyBaselineItem started", { id: id });

			trackPromise(
				dispatch(copyBaselineItem({ data: { id, updatedBy: Session().currUser.id } }))
					.unwrap()
					.then((response) => {
						showDialog(
							{
								title: "Successful",
								message: ["The Baseline Item was duplicated successfully."],
							},
							() => {
								reloadParentBaseline();
								handleBackToList && handleBackToList();
							}
						);

						trackEvent({
							event: `Baseline Item was copied (#${id} to #${response.newId})`,
							props: null,
						});
					})
					.catch((error) => {
						errorLog(2, "[useBaselineItemActions][FLOW] copyBaselineItem failed", {
							id: id,
							error: error,
						});
						console.error("copyBaselineItem failed", error);
						// Item copy is a mutation, so do not offer retry after a
						// failed response; the duplicate item may already exist.
						withError(error, null, {
							fallbackMessage: "Baseline item copy failed.",
						});
					})
			);
		},
		[
			dispatch,
			showDialog,
			reloadParentBaseline,
			handleBackToList,
			trackEvent,
			withError,
		]
	);

	// STATUS CHANGE - archive/restore/delete
	const updateItemStatusAction = useCallback(
		(id, newStatus, showMsgBox = true) => {
			debugLog(3, "[useBaselineItemActions][ACTION] updateItemStatusAction", {
				id: id,
				newStatus: newStatus,
				showMsgBox: showMsgBox,
			});
			debugLog(2, "[useBaselineItemActions][FLOW] updateItemStatus started", {
				id: id,
				newStatus: newStatus,
			});

			let strAction = "archived";
			if (newStatus === 1) strAction = "re-activated";
			else if (newStatus === -1) strAction = "deleted";

			trackPromise(
				dispatch(
					updateItemStatus({
						id,
						status_id: newStatus,
						updatedBy: Session().currUser.id,
					})
				)
					.unwrap()
					.then(() => {
						debugLog(3, "[useBaselineItemActions][STATE] baseline item status updated", {
							id: id,
							newStatus: newStatus,
							action: strAction,
						});

						if (showMsgBox) {
							showDialog(
								{
									title: "Successful",
									message: [`The item "${localItem?.name}" was ${strAction} successfully!`],
								},
								() => {
									reloadParentBaseline();
									handleBackToList && handleBackToList();
								}
							);
						} else {
							reloadParentBaseline();
							handleBackToList && handleBackToList();
						}

						trackEvent({
							event: `Baseline Item (#${id}) was ${strAction}`,
							props: null,
						});
					})
					.catch((error) => {
						errorLog(2, "[useBaselineItemActions][FLOW] updateItemStatus failed", {
							id: id,
							error: error,
						});
						console.error("updateItemStatus failed", error);
						// Item status changes mutate server state, so keep the shared
						// error contract here without offering a retry action.
						withError(error, null, {
							fallbackMessage: "Baseline item status update failed.",
						});
					})
			);
		},
		[
			dispatch,
			localItem,
			showDialog,
			reloadParentBaseline,
			handleBackToList,
			trackEvent,
			withError,
		]
	);

	return {
		createItemAction,
		updateItemAction,
		deleteItemAction,
		copyItemAction,
		updateItemStatusAction,
	};
}
